import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { ToastService } from '../../../shared/services/toast.service';
import { RoleApiService } from '../services/role-api.service';
import {
  AppModule,
  ModulePermission,
  RoleCreateRequest,
  RoleDetail,
  RoleUpdateRequest,
} from '../models/role.model';

/** Fila editable de la matriz de permisos (1 por módulo del catálogo). */
interface PermissionRow {
  moduleCode: string;
  moduleName: string;
  category: string | null;
  orderPosition: number;
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canImport: boolean;
  canExport: boolean;
}

type PermissionAction = 'canView' | 'canCreate' | 'canEdit' | 'canDelete' | 'canImport' | 'canExport';

@Component({
  selector: 'app-role-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, ButtonComponent],
  templateUrl: './role-form.component.html',
})
export class RoleFormComponent {
  @Input() drawerMode = true;

  @Output() saved = new EventEmitter<RoleDetail>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  /** Roles de sistema: solo lectura. */
  readOnly = false;
  roleId: number | null = null;
  private originalName = '';

  modules: AppModule[] = [];
  rows: PermissionRow[] = [];

  readonly actions: { key: PermissionAction; label: string }[] = [
    { key: 'canView', label: 'Ver' },
    { key: 'canCreate', label: 'Crear' },
    { key: 'canEdit', label: 'Editar' },
    { key: 'canDelete', label: 'Borrar' },
    { key: 'canImport', label: 'Importar' },
    { key: 'canExport', label: 'Exportar' },
  ];

  constructor(
    private fb: FormBuilder,
    private api: RoleApiService,
    private toast: ToastService
  ) {
    this.initForm();
  }

  private initForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(50)]],
      description: ['', [Validators.maxLength(255)]],
    });
  }

  /** Reinicia el form para "Nuevo rol" y carga el catálogo de módulos (filas vacías). */
  reset(): void {
    this.isEditing = false;
    this.readOnly = false;
    this.roleId = null;
    this.error = '';
    this.saving = false;
    this.initForm();
    this.loadModules(null);
  }

  /** Carga un rol existente para editar/ver y mergea su matriz con el catálogo. */
  loadRole(id: number, readOnly: boolean): void {
    this.isEditing = true;
    this.readOnly = readOnly;
    this.roleId = id;
    this.error = '';
    this.loading = true;

    forkJoin({
      modules: this.api.getModules(),
      role: this.api.getRole(id),
    }).subscribe({
      next: ({ modules, role }) => {
        this.modules = [...modules].sort(
          (a, b) => (a.orderPosition ?? 0) - (b.orderPosition ?? 0)
        );
        this.form.patchValue({
          name: role.name,
          description: role.description ?? '',
        });
        this.originalName = role.name;
        this.buildRows(role.permissions);
        if (readOnly) this.form.disable();
        else this.form.enable();
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el rol';
        this.loading = false;
      },
    });
  }

  private loadModules(existing: ModulePermission[] | null): void {
    this.loading = true;
    this.form.enable();
    this.api.getModules().subscribe({
      next: (modules) => {
        this.modules = [...modules].sort(
          (a, b) => (a.orderPosition ?? 0) - (b.orderPosition ?? 0)
        );
        this.buildRows(existing ?? []);
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar los módulos';
        this.loading = false;
      },
    });
  }

  /**
   * MERGE: el catálogo de módulos define el conjunto total de filas; se superponen
   * los permisos del rol por moduleCode. Los módulos ausentes quedan todo en false.
   */
  private buildRows(permissions: ModulePermission[]): void {
    const byCode = new Map<string, ModulePermission>();
    for (const p of permissions) byCode.set(p.moduleCode, p);

    this.rows = this.modules.map((m) => {
      const p = byCode.get(m.code);
      return {
        moduleCode: m.code,
        moduleName: m.name,
        category: m.category,
        orderPosition: m.orderPosition ?? 0,
        canView: p?.canView ?? false,
        canCreate: p?.canCreate ?? false,
        canEdit: p?.canEdit ?? false,
        canDelete: p?.canDelete ?? false,
        canImport: p?.canImport ?? false,
        canExport: p?.canExport ?? false,
      };
    });
  }

  // ---- Conveniencias de la matriz ----

  /** Al marcar una celda: si es una acción, fuerza Ver; al desmarcar Ver, limpia el resto. */
  onCellChange(row: PermissionRow, action: PermissionAction, checked: boolean): void {
    if (this.readOnly) return;
    row[action] = checked;
    if (action !== 'canView' && checked) {
      row.canView = true;
    }
    if (action === 'canView' && !checked) {
      row.canCreate = false;
      row.canEdit = false;
      row.canDelete = false;
      row.canImport = false;
      row.canExport = false;
    }
  }

  /** Marca/desmarca toda la fila. */
  toggleRow(row: PermissionRow, checked: boolean): void {
    if (this.readOnly) return;
    row.canView = checked;
    row.canCreate = checked;
    row.canEdit = checked;
    row.canDelete = checked;
    row.canImport = checked;
    row.canExport = checked;
  }

  isRowFull(row: PermissionRow): boolean {
    return row.canView && row.canCreate && row.canEdit && row.canDelete
      && row.canImport && row.canExport;
  }

  /** Marca/desmarca toda una columna (acción) en todas las filas. */
  toggleColumn(action: PermissionAction, checked: boolean): void {
    if (this.readOnly) return;
    for (const row of this.rows) {
      this.onCellChange(row, action, checked);
    }
  }

  isColumnFull(action: PermissionAction): boolean {
    return this.rows.length > 0 && this.rows.every((r) => r[action]);
  }

  guardar(): void {
    if (this.readOnly) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.error = '';

    const v = this.form.value;
    // REEMPLAZO TOTAL: enviar SIEMPRE los 8 flags por fila (incl. canImport/canExport),
    // o el backend los persistiría en false y se perderían.
    const permissions: ModulePermission[] = this.rows.map((r) => ({
      moduleCode: r.moduleCode,
      moduleName: r.moduleName,
      canView: r.canView,
      canCreate: r.canCreate,
      canEdit: r.canEdit,
      canDelete: r.canDelete,
      canImport: r.canImport,
      canExport: r.canExport,
    }));

    const description = (v.description ?? '').trim();

    if (this.isEditing && this.roleId) {
      const body: RoleUpdateRequest = {
        name: v.name.trim(),
        description: description || undefined,
        permissions,
      };
      this.api.updateRole(this.roleId, body).subscribe({
        next: (role) => {
          this.saving = false;
          this.toast.updated('Rol',
            this.originalName !== v.name.trim()
              ? `Se actualizó "${this.originalName}" → "${v.name.trim()}".`
              : `Se actualizó "${v.name.trim()}" correctamente.`);
          this.saved.emit(role);
        },
        error: (err) => this.handleError(err, 'Error al actualizar el rol'),
      });
    } else {
      const body: RoleCreateRequest = {
        name: v.name.trim(),
        description: description || null,
        permissions,
      };
      this.api.createRole(body).subscribe({
        next: (role) => {
          this.saving = false;
          this.toast.created('Rol', `Se creó "${v.name.trim()}" correctamente.`);
          this.saved.emit(role);
        },
        error: (err) => this.handleError(err, 'Error al registrar el rol'),
      });
    }
  }

  private handleError(err: { error?: { message?: string } }, fallback: string): void {
    this.saving = false;
    const msg = err?.error?.message || fallback;
    this.error = msg;
    this.toast.error({ title: 'Error', message: msg });
  }

  cancelar(): void {
    this.cancelled.emit();
  }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!c && c.invalid && c.touched;
  }

  getError(field: string): string {
    const c = this.form.get(field);
    if (!c?.errors) return '';
    if (c.errors['required']) return 'Este campo es requerido';
    if (c.errors['maxlength']) return `Máximo ${c.errors['maxlength'].requiredLength} caracteres`;
    return 'Campo inválido';
  }

  trackRow = (_: number, row: PermissionRow) => row.moduleCode;
}
